// Timeline — merged read-only feed: published releases + feature
// announcements. Filter tabs slice by type/surface/state.
//
// Announcements come from `feature_entries.announcement_state/date` (the
// replacement for the old flag_flip_events log).
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  FlaskConical,
  Globe,
  LayoutGrid,
  type LucideIcon,
  Package,
  Rocket,
  Smartphone,
} from "lucide-react";
import { useSearchParams } from "wouter";
import type {
  EnrichedFeature,
  FeedItem,
  Release,
  ReleaseRollup,
} from "@release-hub/shared";
import { useFeatures, useFeed, useReleases } from "@/api/queries";
import { usePersona } from "@/auth/Persona";
import { useEffectiveRole } from "@/auth/role";
import { BrandMark } from "@/components/BrandMark";
import { NewFeatureLaunchDrawer } from "@/components/NewFeatureLaunchDrawer";
import { ScheduleReleaseDrawer } from "@/components/ScheduleReleaseDrawer";
import {
  FeatureAnnouncementCard,
  BackToTimeline,
  FeatureDetailView,
  ReleaseCard,
  ReleaseDetailView,
  UncuratedReleasesDrawer,
} from "@/components/FeedCard";
import { LoadingLine } from "@/components/LoadingLine";
import { cn } from "@/lib/cn";
import { useLastVisitCutoff } from "@/lib/lastVisit";
import { effectiveReleaseProminence } from "@/lib/releaseProminence";

type FilterKey =
  | "all"
  | "release"
  | "announcement"
  | "unreleased"
  | "web"
  | "mobile";

const FILTER_KEYS = new Set<string>([
  "all",
  "release",
  "announcement",
  "unreleased",
  "web",
  "mobile",
]);
const isFilterKey = (v: string | null): v is FilterKey =>
  v != null && FILTER_KEYS.has(v);

/** Left-to-right order of the views as they sit in the chip row. Used to pick
 *  the enter-slide direction so results fly in from the side you moved toward. */
const VIEW_ORDER = ["all", "release", "web", "mobile", "announcement", "unreleased"];

/** Max number of "New since last visit" badges shown at once — keeps the badge
 *  meaningful after a long absence instead of flagging the entire feed. */
const NEW_BADGE_CAP = 6;

interface FilterDef {
  key: FilterKey;
  label: string;
  icon: LucideIcon;
  /** Only EPD + content authors see this chip (unshipped work). */
  editorOnly?: boolean;
}

// Chips render in three visually separated groups:
//   1. All              — the unfiltered feed
//   2. Releases/Web/Mobile — releases + matching feature launches, by surface
//   3. Feature launches / Unreleased — announcement + staging views
const FILTER_GROUPS: FilterDef[][] = [
  [{ key: "all", label: "All", icon: LayoutGrid }],
  [
    { key: "release", label: "Releases", icon: Package },
    { key: "web", label: "Web", icon: Globe },
    { key: "mobile", label: "Mobile", icon: Smartphone },
  ],
  [
    { key: "announcement", label: "Feature launches", icon: Rocket },
    // The staging area: drafts (features + releases) and launches whose EA
    // date is still in the future. Gated to EPD + content authors
    // (canEditContent) — GTM/viewers don't see unshipped work.
    { key: "unreleased", label: "Unreleased", icon: FlaskConical, editorOnly: true },
  ],
];

function matchesFilter(item: FeedItem, key: FilterKey): boolean {
  if (key === "all") return true;
  if (key === "release") return item.type === "release";
  if (key === "announcement") return item.type === "feature_announcement";
  // "unreleased" is served from feature data, not the dated feed — feed
  // items never match it (the render branches to a separate list).
  if (key === "unreleased") return false;
  if (key === "web") {
    if (item.type === "release")
      return (item.data.builds ?? []).some((b) => b.surface === "web");
    if (item.type === "feature_announcement")
      return item.data.surface.includes("web");
    return false;
  }
  if (key === "mobile") {
    if (item.type === "release")
      return (item.data.builds ?? []).some((b) => b.surface === "mobile");
    if (item.type === "feature_announcement")
      return item.data.surface.some((s) => s === "ios" || s === "android");
    return false;
  }
  return true;
}

/** Compact date for the left rail: month+day on one line, year below.
 *  Falls back to the raw string if it isn't a parseable date. */
function railDate(iso: string): { main: string; year: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { main: iso, year: "" };
  return {
    main: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    year: String(d.getFullYear()),
  };
}

/** Month bucket for a feed item — `key` groups adjacent items, `label` is
 *  the heading shown at each month break in the rail. */
function monthOf(iso: string): { key: string; label: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { key: iso, label: iso };
  return {
    key: `${d.getFullYear()}-${d.getMonth()}`,
    label: d.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
  };
}

/** Timeline items don't share an id type — releases use `rel_…` and
 *  announcements use the feature's id. Combine with the type to avoid
 *  collisions across the two spaces. */
function feedItemKey(item: FeedItem): string {
  if (item.type === "release_rollup") return "release_rollup";
  // A feature can appear twice (EA card + GA card) — disambiguate by state
  // so the two rows get distinct keys / selection ids.
  if (item.type === "feature_announcement") {
    return `${item.type}:${item.data.id}:${item.data.announcement_state}`;
  }
  return `${item.type}:${item.data.id}`;
}

export function Timeline() {
  const { data: feed, isPending, error } = useFeed();
  const { persona } = usePersona();
  const { isEditor, canEditContent } = useEffectiveRole();
  // Features power the "Unreleased" tab — not-yet-GA launches that aren't on
  // the dated feed. Only fetched/shown for EPD + content authors.
  const { data: features } = useFeatures();
  // Draft releases join the same tab: they're unpublished, so they're absent
  // from the feed, the Timeline, and search — this is where they surface.
  const { data: draftReleases } = useReleases("draft");
  // Filter lives in the URL (?f=web) so it survives a card round-trip and the
  // back button, and is deep-linkable. Absent/invalid → "all".
  const [searchParams, setSearchParams] = useSearchParams();
  const rawFilter = searchParams.get("f");
  let filter: FilterKey = isFilterKey(rawFilter) ? rawFilter : "all";
  // A stale or deep-linked ?f=unreleased must not strand a non-editor on the
  // editor-only staging view (they'd hit the wrong empty state). Fall back.
  if (filter === "unreleased" && !canEditContent) filter = "all";
  const setFilter = (key: FilterKey) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === "all") next.delete("f");
        else next.set("f", key);
        return next;
      },
      { replace: true },
    );

  // Detail view state lives in the URL: ?release=<id> or ?feature=<id>
  // swaps the feed for that item's full-page detail. Keeping it on the
  // Timeline route means copied links keep working and Back returns to the
  // feed (with its filter) at the same scroll position.
  const openReleaseId = searchParams.get("release");
  const openFeatureId = searchParams.get("feature");
  const detailOpen = !!(openReleaseId || openFeatureId);

  // Feed scroll position at the moment a detail opened from it. Null when
  // the detail was the landing page (a shared link), so Back has no feed
  // history entry to pop and starts the feed at the top instead.
  const feedScrollRef = useRef<number | null>(null);

  const openDetail = (kind: "release" | "feature", id: string) => {
    feedScrollRef.current = window.scrollY;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("release");
        next.delete("feature");
        next.set(kind, id);
        return next;
      },
      { replace: false },
    );
  };
  const backToFeed = () => {
    if (feedScrollRef.current !== null) {
      window.history.back();
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("release");
        next.delete("feature");
        return next;
      },
      { replace: true },
    );
  };

  // Detail opens at the top; the feed comes back where you left it. Layout
  // effect so the restore lands before paint instead of flashing the top.
  useLayoutEffect(() => {
    if (detailOpen) {
      window.scrollTo(0, 0);
    } else if (feedScrollRef.current !== null) {
      window.scrollTo(0, feedScrollRef.current);
      feedScrollRef.current = null;
    }
  }, [detailOpen, openReleaseId, openFeatureId]);

  // Esc goes back, unless a modal (export, workspaces) has the key.
  useEffect(() => {
    if (!detailOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      backToFeed();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // backToFeed only reads a ref and the URL; re-binding per render is pointless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailOpen]);

  // Cutoff for "new since last visit" badges. Snapshotted on mount so
  // items you land to see stay flagged as new until you leave the page.
  const lastVisitCutoff = useLastVisitCutoff("timeline");

  // The uncurated-release rollup is pulled out of the chronological feed and
  // surfaced via a toolbar drawer instead — raw build noise shouldn't sit in
  // the timeline itself. There's at most one rollup item.
  const rollup = useMemo(
    () =>
      (feed ?? []).find((it) => it.type === "release_rollup")?.data as
        | ReleaseRollup
        | undefined,
    [feed],
  );

  const filtered = useMemo(() => {
    return (feed ?? []).filter((it) => {
      if (it.type === "release_rollup") return false;
      if (!matchesFilter(it, filter)) return false;
      // Sales persona: minor releases are noise. Drop them entirely — the
      // user asked for "big things this quarter" framing, not patch chatter.
      if (
        persona === "sales" &&
        it.type === "release" &&
        effectiveReleaseProminence(it.data) === "minor"
      ) {
        return false;
      }
      return true;
    });
  }, [feed, filter, persona]);

  // Features for the "Unreleased" tab — everything that hasn't shipped a
  // CONCRETE milestone yet. That means: drafts, plus any published feature
  // whose dates are only fuzzy targets (TBD / Q:… / W:…) or concrete dates
  // still in the future (and, as a safety net, features with no dates at
  // all — so a published feature can never vanish from every view). Once
  // ea_date OR ga_date is a concrete past/present date, that milestone has
  // shipped and the feature lives on the main feed instead.
  const unreleased = useMemo(() => {
    const isConcrete = (v: string | null) => !!v && /^\d{4}-/.test(v);
    const shipped = (v: string | null) => {
      if (!isConcrete(v)) return false; // null / "TBD" / "Q:…" → not shipped
      const t = Date.parse(v!);
      return Number.isFinite(t) && t <= Date.now();
    };
    return (features ?? []).filter(
      (f) =>
        f.status === "draft" ||
        !(shipped(f.ea_date) || shipped(f.ga_date)),
    );
  }, [features]);
  const showUnreleased = filter === "unreleased" && canEditContent;

  // Unreleased tab ordering: everything with a concrete date sorts first
  // (soonest at the top — that's what you're tracking), undated work after.
  // Features and draft releases interleave in one list so a scheduled
  // release for Monday isn't buried under undated feature ideas.
  const unreleasedRows = useMemo(() => {
    const isConcrete = (v: string | null | undefined) =>
      !!v && /^\d{4}-/.test(v);
    const rows: { key: string; date: string | null; node: "feature" | "release"; item: unknown }[] = [];
    for (const f of unreleased) {
      const dates = [f.ea_date, f.ga_date].filter(isConcrete).sort() as string[];
      rows.push({ key: `f-${f.id}`, date: dates[0] ?? null, node: "feature", item: f });
    }
    for (const r of draftReleases ?? []) {
      const date =
        r.release_date_override ??
        (r.builds ?? []).map((b) => b.published_at).sort()[0] ??
        null;
      rows.push({ key: `r-${r.id}`, date, node: "release", item: r });
    }
    rows.sort((a, b) => {
      if (a.date && b.date) return a.date.localeCompare(b.date);
      if (a.date) return -1;
      if (b.date) return 1;
      return 0; // stable: undated keep their original relative order
    });
    return rows;
  }, [unreleased, draftReleases]);

  // Resolve the detail's item from data already loaded: the feed first (its
  // feature rows carry the announcement state), then drafts / all features
  // so Unreleased-tab items and deep links into them resolve too.
  const selectedRelease = useMemo<Release | undefined>(() => {
    if (!openReleaseId) return undefined;
    for (const it of feed ?? []) {
      if (it.type === "release" && it.data.id === openReleaseId) return it.data;
    }
    return draftReleases?.find((r) => r.id === openReleaseId);
  }, [openReleaseId, feed, draftReleases]);
  const selectedFeature = useMemo<EnrichedFeature | undefined>(() => {
    if (!openFeatureId) return undefined;
    for (const it of feed ?? []) {
      if (it.type === "feature_announcement" && it.data.id === openFeatureId) {
        return it.data;
      }
    }
    return features?.find((f) => f.id === openFeatureId);
  }, [openFeatureId, feed, features]);

  // Cap the "New since last visit" badges to the most recent few items. After a
  // long absence the whole feed would otherwise light up — and when everything
  // is new, nothing is. `filtered` is newest-first, so the first N new items
  // are the latest ones.
  const newKeys = useMemo(() => {
    const keys = new Set<string>();
    if (lastVisitCutoff == null) return keys;
    for (const it of filtered) {
      if (it.sort_date > lastVisitCutoff) {
        keys.add(feedItemKey(it));
        if (keys.size >= NEW_BADGE_CAP) break;
      }
    }
    return keys;
  }, [filtered, lastVisitCutoff]);

  // Direction-aware enter: compare the new view's position in the chip row to
  // the previous one and slide in from the side we moved toward. prevViewRef
  // still holds the old view during this render (it's updated in the effect
  // after paint), which is exactly what we compare against.
  const viewKey = showUnreleased ? "unreleased" : filter;
  const prevViewRef = useRef(viewKey);
  const resultsRef = useRef<HTMLDivElement>(null);
  const enterDir =
    VIEW_ORDER.indexOf(viewKey) < VIEW_ORDER.indexOf(prevViewRef.current)
      ? "slide-in-from-left-4"
      : "slide-in-from-right-4";
  // Replay the enter animation on view change WITHOUT remounting the card list.
  // Keying the wrapper would rebuild every card (and its dialog) each switch —
  // heavy synchronous work that drops the animation's first frames. Instead we
  // let React reconcile the list in place (cheap; persisting cards are reused)
  // and restart the CSS animation by clearing it and forcing a reflow.
  //
  // useLayoutEffect (not useEffect) so the restart happens BEFORE the browser
  // paints: otherwise the new content paints once at its resting position and
  // then snaps back to the animation's start, which reads as a jitter.
  useLayoutEffect(() => {
    prevViewRef.current = viewKey;
    const el = resultsRef.current;
    if (!el) return;
    el.style.animation = "none";
    void el.offsetWidth; // reflow so re-adding the class restarts from frame 0
    el.style.animation = "";
  }, [viewKey]);

  if (isPending) {
    return <LoadingLine />;
  }
  if (error) {
    return (
      <p className="py-6 text-destructive">
        Failed to load feed: {error instanceof Error ? error.message : "unknown"}
      </p>
    );
  }

  if (detailOpen) {
    if (selectedRelease) {
      return <ReleaseDetailView data={selectedRelease} onBack={backToFeed} />;
    }
    if (selectedFeature) {
      return <FeatureDetailView data={selectedFeature} onBack={backToFeed} />;
    }
    if (features === undefined || draftReleases === undefined) {
      return <LoadingLine />;
    }
    return <MissingDetail onBack={backToFeed} />;
  }

  return (
    <div className="animate-in fade-in-0 space-y-6 py-6 duration-300">
      <div className="flex items-center gap-3">
        <BrandMark className="h-8 w-8 shrink-0" />
        <h1 className="font-serif text-3xl">Release Hub</h1>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <nav aria-label="Filter feed" className="flex flex-wrap items-center gap-1">
          {FILTER_GROUPS.map((group, gi) => {
            const visible = group.filter(
              (f) => !f.editorOnly || canEditContent,
            );
            if (visible.length === 0) return null;
            return (
              <Fragment key={gi}>
                {gi > 0 && (
                  <span aria-hidden className="mx-1 h-4 w-px bg-border" />
                )}
                {visible.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    aria-pressed={filter === key}
                    className={cn(
                      // Selected = a lifted Filed White sheet (depth marks the
                      // active control per the Filed-Flat rule), not the solid
                      // ink "commit" treatment which out-shouts the soft state
                      // pills below. Transparent border on rest keeps metrics
                      // stable across the toggle.
                      "inline-flex items-center gap-1.5 rounded-md border px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50",
                      filter === key
                        ? "border-border-hover bg-card text-text shadow-sm"
                        : "border-transparent text-text-secondary hover:bg-border/50 hover:text-text",
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                ))}
              </Fragment>
            );
          })}
        </nav>
        <span className="ml-auto flex items-center gap-3">
          {/* Result count — makes filtering visible (switching to "Mobile"
              silently dropping 36→n was a status gap). aria-live announces the
              change to screen readers. */}
          {!showUnreleased && filtered.length > 0 && (
            <span className="text-[12.5px] text-text-tertiary" aria-live="polite">
              {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
            </span>
          )}
          {/* Both create affordances are editor-only. Content Authors + GTM
              curate copy, they don't mint new releases or launches. */}
          {isEditor && rollup && rollup.count > 0 && (
            <UncuratedReleasesDrawer data={rollup} />
          )}
          {isEditor && <ScheduleReleaseDrawer />}
          {isEditor && <NewFeatureLaunchDrawer />}
        </span>
      </div>

      {/* Keyed by the active view so switching filters remounts the results
          and replays a fade + rise — the "switch" animation. Chips stay
          mounted (outside this wrapper) so keyboard focus isn't lost. */}
      <div
        ref={resultsRef}
        className={cn(
          "animate-in fade-in-0 duration-200 ease-out will-change-transform",
          enterDir,
        )}
      >
      {showUnreleased ? (
        unreleased.length === 0 && (draftReleases?.length ?? 0) === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-card/40 px-6 py-14 text-center text-text-tertiary">
            Nothing unreleased — every launch is GA and every release is published.
          </div>
        ) : (
          <div className="space-y-3">
            {unreleasedRows.map((row) => {
              if (row.node === "feature") {
                const feature = row.item as (typeof unreleased)[number];
                return (
                  <FeatureAnnouncementCard
                    key={row.key}
                    data={feature}
                    onOpen={() => openDetail("feature", feature.id)}
                  />
                );
              } else {
                const release = row.item as NonNullable<typeof draftReleases>[number];
                return (
                  <ReleaseCard
                    key={row.key}
                    data={release}
                    onOpen={() => openDetail("release", release.id)}
                  />
                );
              }
            })}
          </div>
        )
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card/40 px-6 py-14 text-center text-text-tertiary">
          Nothing in the Release Hub yet.
        </div>
      ) : (
        <div className="relative">
          {/* Continuous timeline spine the per-item dots sit on. Inset top
              and bottom so it starts/ends at the first/last dot. Desktop only:
              below md the rail collapses and the date moves inline. */}
          <span
            aria-hidden
            className="pointer-events-none absolute bottom-4 left-16 top-4 hidden w-px bg-border md:block"
          />
          <ol className="space-y-3">
            {filtered.map((item, idx) => {
              const key = feedItemKey(item);
              // "New since your last visit" — capped to the most recent few
              // (see newKeys). Nothing is new on a first-ever visit.
              const isNew = newKeys.has(key);
              const when = railDate(item.sort_date);
              // A month heading precedes the first item of each month, with
              // a gap in the spine so months read as distinct groups.
              const month = monthOf(item.sort_date);
              const prevKey =
                idx > 0 ? monthOf(filtered[idx - 1]!.sort_date).key : null;
              const newMonth = month.key !== prevKey;
              const card =
                item.type === "release" ? (
                  <ReleaseCard
                    data={item.data}
                    hideDate
                    onOpen={() => openDetail("release", item.data.id)}
                  />
                ) : item.type === "feature_announcement" ? (
                  <FeatureAnnouncementCard
                    data={item.data}
                    onOpen={() => openDetail("feature", item.data.id)}
                  />
                ) : null;
              return (
                <Fragment key={key}>
                  {newMonth && (
                    <li className="relative pl-0 pt-4 first:pt-0 md:pl-24">
                      {/* Mask a slice of the spine for a clean break (desktop
                          rail only). */}
                      <span
                        aria-hidden
                        className="absolute -top-3 bottom-0 left-16 hidden w-3 -translate-x-1/2 bg-bg md:block"
                      />
                      <div className="font-serif text-lg font-semibold tracking-tight text-text-secondary">
                        {month.label}
                      </div>
                    </li>
                  )}
                  <li className="relative pl-0 md:pl-24">
                    {/* Date: an inline line above the card on mobile (full-width
                        copy, no gutter), promoted to the left rail gutter on
                        md+ where the spine appears. The year is dropped — the
                        month heading above each group already carries it, so
                        repeating it under every date was pure gutter noise. */}
                    <div className="mb-1.5 text-[12.5px] font-medium text-text-secondary md:absolute md:left-0 md:top-4 md:mb-0 md:w-14 md:text-right md:text-sm md:leading-tight">
                      {when.main}
                    </div>
                    {/* Dot on the spine — accent when new, so the rail doubles
                        as a "what's new" scan. Desktop rail only. */}
                    <span
                      aria-hidden
                      className={cn(
                        "absolute left-16 top-5 z-10 hidden h-2.5 w-2.5 -translate-x-1/2 rounded-full ring-4 ring-bg md:block",
                        isNew ? "bg-text" : "bg-border-hover",
                      )}
                    />
                    <div className="relative">
                      {isNew && (
                        <span
                          className="absolute -top-2 left-3 z-10 rounded-full bg-text px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-bg shadow-sm"
                          title="Published since your last visit"
                        >
                          New
                        </span>
                      )}
                      {card}
                    </div>
                  </li>
                </Fragment>
              );
            })}
          </ol>
        </div>
      )}
      </div>
    </div>
  );
}

/** Detail body for a ?release= / ?feature= id we can't resolve: deleted,
 *  hidden from this persona, or not loaded yet. */
function MissingDetail({ onBack }: { onBack: () => void }) {
  return (
    <div className="mx-auto max-w-3xl py-6">
      <BackToTimeline onBack={onBack} />
      <p className="mt-10 text-center text-sm text-text-tertiary">
        This item isn't available. It may be hidden, unpublished, or removed.
      </p>
    </div>
  );
}
